import { useState, useRef, useEffect } from "react";
import { useClients, useCreateClient, useUpdateClient } from "../../../../hooks/useWorkshopQueries";

// ─── Constants ────────────────────────────────────────────────────────────────
const INSURERS = [
  "HDFC Ergo","Bajaj Allianz","ICICI Lombard","New India Assurance",
  "Oriental Insurance","United India","Star Health","Reliance General",
];

const EMPTY_FORM = {
  clientName:"", clientShort:"", contactPerson:"", contactPhone:"",
  primaryEmail:"", ccEmails:[],  isActive:true,
};

// ─── Main ClientsTab ──────────────────────────────────────────────────────────
export default function ClientsTab() {
  const { data: clients = [], isLoading } = useClients();
  const { mutateAsync: createClient } = useCreateClient();
  const { mutateAsync: updateClient } = useUpdateClient();

  const [search, setSearch]           = useState("");
  const [filterActive, setFilterActive] = useState("all");
  const [modalOpen, setModalOpen]     = useState(false);
  const [editTarget, setEditTarget]   = useState(null);
  const [saving, setSaving]           = useState(false);
  const [viewClient, setViewClient]   = useState(null);

  const filtered = clients.filter(c => {
    const matchSearch = !search || [c.clientName, c.clientShort, c.contactPerson, c.primaryEmail]
      .some(f => f?.toLowerCase().includes(search.toLowerCase()));
    const matchStatus = filterActive === "all" || (filterActive === "active" ? c.isActive : !c.isActive);
    return matchSearch && matchStatus;
  });

  const openAdd  = () => { setEditTarget(null); setModalOpen(true); };
  const openEdit = (c) => { setEditTarget(c);   setModalOpen(true); };

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (editTarget) {
        await updateClient({ id: editTarget.id, ...formData });
      } else {
        await createClient(formData);
      }
      setModalOpen(false);
    } catch (err) {
      console.error("Save client failed:", err);
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) return (
    <div style={{ padding:40, textAlign:"center", color:"var(--wd-t3)" }}>Loading...</div>
  );

  const totalActive = clients.filter(c => c.isActive).length;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

      {/* ── Summary KPIs ── */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
        {[
          { label:"Total Clients",      value:clients.length,                                color:"#3b82f6" },
          { label:"Active",             value:totalActive,                                   color:"#22c55e" },
          { label:"Inactive",           value:clients.length - totalActive,                  color:"#94a3b8" },
          { label:"Total Active Cases", value:clients.reduce((s,c)=>s+(c.activeCases||0),0), color:"#f97316" },
        ].map(({ label, value, color }) => (
          <div key={label} className="wd-card" style={{ padding:"14px 12px", borderLeft:`3px solid ${color}` }}>
            <div style={{ fontSize:10, color:"var(--wd-t3)", textTransform:"uppercase", letterSpacing:".05em", fontWeight:600, marginBottom:5 }}>{label}</div>
            <div style={{ fontSize:22, fontWeight:800, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* ── Toolbar ── */}
      <div style={{ display:"flex", gap:8, alignItems:"center" }}>
        <div style={{ position:"relative", flex:1, maxWidth:340 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:"var(--wd-t3)", pointerEvents:"none" }}>
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            className="wd-input"
            style={{ paddingLeft:32 }}
            placeholder="Search clients…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div style={{ display:"flex", gap:0, border:"1px solid var(--wd-border)", borderRadius:6, overflow:"hidden" }}>
          {[["all","All"],["active","Active"],["inactive","Inactive"]].map(([val,label]) => (
            <button key={val}
              onClick={() => setFilterActive(val)}
              style={{
                padding:"6px 14px", fontSize:12, fontWeight:600, border:"none", cursor:"pointer",
                background: filterActive === val ? "var(--wd-blue)" : "transparent",
                color: filterActive === val ? "#fff" : "var(--wd-t2)",
                transition:"all .15s",
              }}
            >{label}</button>
          ))}
        </div>
        <div style={{ flex:1 }} />
        <button className="wd-btn wd-btn-primary" onClick={openAdd} style={{ display:"flex", alignItems:"center", gap:6 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add Client
        </button>
      </div>

      {/* ── Clients Table ── */}
      <div className="wd-card" style={{ overflow:"hidden" }}>
        <div className="wd-table-wrap">
          <table className="wd-table">
            <thead>
              <tr>
                <th>CLIENT</th>
                <th>CODE</th>
                <th>CONTACT</th>
                <th>PRIMARY EMAIL</th>
                <th>CC EMAILS</th>
                <th>ACTIVE CASES</th>
                <th>STATUS</th>
                <th style={{ width:80 }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign:"center", color:"var(--wd-t3)", padding:"32px 0", fontSize:13 }}>
                    No clients found
                  </td>
                </tr>
              )}
              {filtered.map(c => (
                <tr key={c.id} style={{ cursor:"pointer" }} onClick={() => setViewClient(c)}>
                  <td>
                    <div style={{ fontWeight:700, fontSize:13 }}>{c.clientName}</div>
                    <div style={{ fontSize:11, color:"var(--wd-t3)", marginTop:2 }}>{c.contactPerson}</div>
                  </td>
                  <td>
                    <span style={{ fontFamily:"var(--wd-mono)", fontSize:12, fontWeight:700,
                      background:"var(--wd-hover)", padding:"2px 7px", borderRadius:4 }}>
                      {c.clientShort}
                    </span>
                  </td>
                  <td>
                    <div style={{ fontSize:12, color:"var(--wd-t2)" }}>{c.contactPhone || "—"}</div>
                  </td>
                  <td>
                    <div style={{ fontSize:12, color:"var(--wd-blue)", fontFamily:"var(--wd-mono)" }}>
                      {c.primaryEmail || "—"}
                    </div>
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <CCBadges emails={c.ccEmails || []} onEdit={() => openEdit(c)} />
                  </td>
                  <td>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <span style={{ fontWeight:700, color:"#f97316" }}>{c.activeCases ?? 0}</span>
                      <span style={{ fontSize:11, color:"var(--wd-t3)" }}>/ {c.totalCases ?? 0}</span>
                    </div>
                  </td>
                  <td>
                    <StatusPill active={c.isActive} />
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <div style={{ display:"flex", gap:4 }}>
                      <button className="wd-btn wd-btn-ghost"
                        style={{ padding:"4px 8px", fontSize:11 }}
                        onClick={() => openEdit(c)}>
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      {modalOpen && (
        <ClientModal
          client={editTarget}
          onSave={handleSave}
          onClose={() => setModalOpen(false)}
          saving={saving}
        />
      )}

      {/* ── Detail Drawer ── */}
      {viewClient && (
        <ClientDrawer
          client={viewClient}
          onClose={() => setViewClient(null)}
          onEdit={() => { setViewClient(null); openEdit(viewClient); }}
        />
      )}
    </div>
  );
}

// ─── CC Badges ────────────────────────────────────────────────────────────────
function CCBadges({ emails, onEdit }) {
  if (!emails.length) return (
    <button onClick={onEdit}
      style={{ fontSize:11, color:"var(--wd-t3)", background:"none",
        border:"1px dashed var(--wd-border)", borderRadius:4,
        padding:"2px 8px", cursor:"pointer", opacity:.7 }}>
      + Add CC
    </button>
  );
  return (
    <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
      {emails.slice(0, 2).map(email => (
        <span key={email} style={{
          fontSize:10, background:"var(--wd-hover)",
          color:"var(--wd-t2)",
          border:"1px solid var(--wd-border)",
          padding:"2px 7px", borderRadius:4,
          fontFamily:"var(--wd-mono)", fontWeight:500,
          maxWidth:140, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
          display:"inline-block",
        }}>
          {email}
        </span>
      ))}
      {emails.length > 2 && (
        <span style={{ fontSize:10, background:"var(--wd-hover)", color:"var(--wd-t3)",
          border:"1px solid var(--wd-border)",
          padding:"2px 7px", borderRadius:4, fontWeight:600 }}>
          +{emails.length - 2}
        </span>
      )}
    </div>
  );
}

// ─── Status Pill ──────────────────────────────────────────────────────────────
function StatusPill({ active }) {
  return (
    <span style={{
      display:"inline-flex", alignItems:"center", gap:5,
      fontSize:11, fontWeight:700, padding:"3px 9px", borderRadius:12,
      background: active ? "#dcfce7" : "#f1f5f9",
      color: active ? "#15803d" : "#64748b",
    }}>
      <span style={{ width:5, height:5, borderRadius:"50%", background: active ? "#22c55e" : "#94a3b8" }} />
      {active ? "Active" : "Inactive"}
    </span>
  );
}

// ─── Client Modal (Add / Edit) ────────────────────────────────────────────────
function ClientModal({ client, onSave, onClose, saving }) {
  const [form, setForm] = useState(client ? {
    clientName:    client.clientName    || "",
    clientShort:   client.clientShort   || "",
    contactPerson: client.contactPerson || "",
    contactPhone:  client.contactPhone  || "",
    primaryEmail:  client.primaryEmail  || "",
    ccEmails:      client.ccEmails      || [],
    // insurerName:   client.insurerName   || "",
    isActive:      client.isActive ?? true,
  } : { ...EMPTY_FORM });

  const [ccInput, setCcInput] = useState("");
  const [ccError, setCcError] = useState("");
  const inputRef = useRef();

  useEffect(() => { inputRef.current?.focus(); }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const addCC = () => {
    const val = ccInput.trim().toLowerCase();
    if (!val) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) { setCcError("Invalid email address"); return; }
    if (form.ccEmails.includes(val)) { setCcError("Already added"); return; }
    set("ccEmails", [...form.ccEmails, val]);
    setCcInput("");
    setCcError("");
  };

  const removeCC = (email) => set("ccEmails", form.ccEmails.filter(e => e !== email));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.clientName.trim()) return;
    onSave(form);
  };

  return (
    <div style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,.45)", zIndex:1000,
      display:"flex", alignItems:"center", justifyContent:"center", padding:20,
    }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background:"var(--wd-card)", borderRadius:12, width:"100%", maxWidth:600,
        boxShadow:"0 20px 60px rgba(0,0,0,.2)", maxHeight:"90vh", overflowY:"auto",
      }}>
        {/* Modal Header */}
        <div style={{ padding:"20px 24px 0", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div>
            <div style={{ fontSize:16, fontWeight:800, color:"var(--wd-t1)" }}>
              {client ? "Edit Client" : "Add New Client"}
            </div>
            <div style={{ fontSize:12, color:"var(--wd-t3)", marginTop:2 }}>
              CC emails are used when sending TCI reports via mail
            </div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer",
            color:"var(--wd-t3)", fontSize:20, lineHeight:1, padding:4 }}>✕</button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding:24 }}>
          {/* Section: Basic Info */}
          <SectionLabel>Client Information</SectionLabel>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 140px", gap:12, marginBottom:12 }}>
            <FormField label="Client Name *">
              <input ref={inputRef} className="wd-input" required
                value={form.clientName} onChange={e => set("clientName", e.target.value)}
                placeholder="e.g. Tata Motors Ltd" />
            </FormField>
            <FormField label="Short Code *">
              <input className="wd-input" required style={{ textTransform:"uppercase", fontFamily:"var(--wd-mono)", fontWeight:700 }}
                value={form.clientShort} onChange={e => set("clientShort", e.target.value.toUpperCase())}
                placeholder="TATA" maxLength={8} />
            </FormField>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
            <FormField label="Contact Person">
              <input className="wd-input"
                value={form.contactPerson} onChange={e => set("contactPerson", e.target.value)}
                placeholder="Ramesh Iyer" />
            </FormField>
            <FormField label="Contact Phone">
              <input className="wd-input" type="tel"
                value={form.contactPhone} onChange={e => set("contactPhone", e.target.value)}
                placeholder="9876543210" />
            </FormField>
          </div>

          {/* <FormField label="Insurer" style={{ marginBottom:12 }}>
            <select className="wd-input wd-select"
              value={form.insurerName} onChange={e => set("insurerName", e.target.value)}>
              <option value="">Select insurer…</option>
              {INSURERS.map(ins => <option key={ins} value={ins}>{ins}</option>)}
            </select>
          </FormField> */}

          {/* Section: Email */}
          <SectionLabel style={{ marginTop:20 }}>Email Configuration</SectionLabel>
          <FormField label="Primary Email *" style={{ marginBottom:12 }}>
            <input className="wd-input" type="email" required
              value={form.primaryEmail} onChange={e => set("primaryEmail", e.target.value)}
              placeholder="contact@client.com" />
          </FormField>

          {/* CC Emails */}
          <FormField label="CC Emails (TCI mail recipients)" style={{ marginBottom:4 }}>
            <div style={{ display:"flex", gap:8 }}>
              <input className="wd-input" type="email"
                value={ccInput} onChange={e => { setCcInput(e.target.value); setCcError(""); }}
                placeholder="cc@client.com"
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCC(); } }}
              />
              <button type="button" className="wd-btn wd-btn-ghost"
                onClick={addCC} style={{ whiteSpace:"nowrap" }}>
                + Add
              </button>
            </div>
          </FormField>

          {ccError && <div style={{ fontSize:11, color:"var(--wd-red)", marginBottom:6 }}>{ccError}</div>}

          {/* CC Tags */}
          {form.ccEmails.length > 0 && (
            <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:8, padding:"10px 12px",
              background:"var(--wd-hover)", borderRadius:8, marginBottom:12 }}>
              {form.ccEmails.map(email => (
                <span key={email} style={{
                  display:"inline-flex", alignItems:"center", gap:6,
                  fontSize:11, background:"var(--wd-hover)", color:"var(--wd-t2)",
                  border:"1px solid var(--wd-border)",
                  fontFamily:"var(--wd-mono)",
                  padding:"3px 8px 3px 10px", borderRadius:6, fontWeight:500,
                }}>
                  {email}
                  <button type="button" onClick={() => removeCC(email)}
                    style={{ background:"none", border:"none", cursor:"pointer",
                      color:"var(--wd-t3)", fontSize:13, lineHeight:1, padding:0 }}>✕</button>
                </span>
              ))}
            </div>
          )}
          {form.ccEmails.length === 0 && (
            <div style={{ fontSize:11, color:"var(--wd-t3)", marginBottom:12, marginTop:4 }}>
              No CC emails added — only the primary email will receive TCI reports
            </div>
          )}

          {/* Active Toggle */}
          {client && (
            <div style={{ display:"flex", alignItems:"center", gap:10, marginTop:4, marginBottom:4 }}>
              <label style={{ fontSize:13, color:"var(--wd-t2)", fontWeight:600 }}>Status</label>
              <button type="button"
                onClick={() => set("isActive", !form.isActive)}
                style={{
                  display:"flex", alignItems:"center", gap:8, background:"none",
                  border:"1px solid var(--wd-border)", borderRadius:20, padding:"4px 12px",
                  cursor:"pointer", fontSize:12, fontWeight:700,
                  color: form.isActive ? "#15803d" : "#64748b",
                }}>
                <span style={{ width:8, height:8, borderRadius:"50%",
                  background: form.isActive ? "#22c55e" : "#94a3b8" }} />
                {form.isActive ? "Active" : "Inactive"}
              </button>
            </div>
          )}

          {/* Actions */}
          <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:20,
            paddingTop:16, borderTop:"1px solid var(--wd-border)" }}>
            <button type="button" className="wd-btn wd-btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="wd-btn wd-btn-primary" disabled={saving}>
              {saving ? "Saving…" : client ? "Save Changes" : "Add Client"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Client Detail Drawer ─────────────────────────────────────────────────────
function ClientDrawer({ client, onClose, onEdit }) {
  return (
    <div style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,.35)", zIndex:1000,
      display:"flex", justifyContent:"flex-end",
    }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width:420, background:"var(--wd-card)", height:"100%", overflowY:"auto",
        boxShadow:"-8px 0 40px rgba(0,0,0,.15)", display:"flex", flexDirection:"column",
      }}>
        {/* Drawer Header */}
        <div style={{ padding:"20px 20px 16px", borderBottom:"1px solid var(--wd-border)",
          display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12 }}>
          <div>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
              <div style={{ width:36, height:36, borderRadius:8, background:"#eff6ff",
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:13, fontWeight:800, color:"#1d4ed8", flexShrink:0 }}>
                {client.clientShort?.slice(0,2) || "CL"}
              </div>
              <div>
                <div style={{ fontSize:15, fontWeight:800, color:"var(--wd-t1)" }}>{client.clientName}</div>
                <div style={{ fontSize:11, fontFamily:"var(--wd-mono)", color:"var(--wd-t3)", fontWeight:600 }}>{client.clientShort}</div>
              </div>
            </div>
            <StatusPill active={client.isActive} />
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer",
            color:"var(--wd-t3)", fontSize:18, padding:4 }}>✕</button>
        </div>

        {/* Stats */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, padding:16,
          borderBottom:"1px solid var(--wd-border)" }}>
          {[
            { label:"Active Cases", value:client.activeCases ?? 0, color:"#f97316" },
            { label:"Total Cases",  value:client.totalCases  ?? 0, color:"#3b82f6" },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ background:"var(--wd-hover)", borderRadius:8, padding:"10px 12px",
              borderLeft:`3px solid ${color}` }}>
              <div style={{ fontSize:10, color:"var(--wd-t3)", textTransform:"uppercase", fontWeight:600, marginBottom:3 }}>{label}</div>
              <div style={{ fontSize:20, fontWeight:800, color }}>{value}</div>
            </div>
          ))}
        </div>

        {/* Details */}
        <div style={{ padding:16, display:"flex", flexDirection:"column", gap:14, flex:1 }}>
          <DrawerSection title="Contact Details">
            <DrawerRow label="Person"  value={client.contactPerson || "—"} />
            <DrawerRow label="Phone"   value={client.contactPhone || "—"} />
          </DrawerSection>

          <DrawerSection title="Email Configuration">
            <DrawerRow label="Primary" value={
              <span style={{ fontFamily:"var(--wd-mono)", fontSize:12, color:"#3b82f6" }}>
                {client.primaryEmail || "—"}
              </span>
            } />
            <div>
              <div style={{ fontSize:11, color:"var(--wd-t3)", fontWeight:600, textTransform:"uppercase",
                letterSpacing:".04em", marginBottom:6 }}>CC Recipients</div>
              {(client.ccEmails || []).length === 0 ? (
                <div style={{ fontSize:12, color:"var(--wd-t3)", fontStyle:"italic" }}>No CC emails configured</div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                  {client.ccEmails.map(email => (
                    <div key={email} style={{ display:"flex", alignItems:"center", gap:8,
                      background:"var(--wd-hover)", border:"1px solid var(--wd-border)",
                      borderRadius:6, padding:"5px 10px" }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--wd-t3)" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                      <span style={{ fontSize:11, fontFamily:"var(--wd-mono)", color:"var(--wd-t2)", fontWeight:500 }}>{email}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DrawerSection>

          <DrawerSection title="Meta">
            <DrawerRow label="Client Since" value={client.createdAt || "—"} />
          </DrawerSection>
        </div>

        {/* Drawer Footer */}
        <div style={{ padding:16, borderTop:"1px solid var(--wd-border)", display:"flex", gap:8 }}>
          <button className="wd-btn wd-btn-ghost" onClick={onClose} style={{ flex:1 }}>Close</button>
          <button className="wd-btn wd-btn-primary" onClick={onEdit} style={{ flex:1 }}>Edit Client</button>
        </div>
      </div>
    </div>
  );
}

// ─── Small UI helpers ─────────────────────────────────────────────────────────
function SectionLabel({ children, style }) {
  return (
    <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:".06em",
      color:"var(--wd-t3)", marginBottom:10, ...style }}>
      {children}
    </div>
  );
}

function FormField({ label, children, style }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:5, ...style }}>
      <label style={{ fontSize:11, fontWeight:700, color:"var(--wd-t2)", textTransform:"uppercase", letterSpacing:".04em" }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function DrawerSection({ title, children }) {
  return (
    <div>
      <div style={{ fontSize:11, fontWeight:700, color:"var(--wd-t3)", textTransform:"uppercase",
        letterSpacing:".05em", marginBottom:10 }}>{title}</div>
      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>{children}</div>
    </div>
  );
}

function DrawerRow({ label, value }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
      fontSize:12, padding:"4px 0", borderBottom:"1px solid var(--wd-border)" }}>
      <span style={{ color:"var(--wd-t3)", fontWeight:600 }}>{label}</span>
      <span style={{ color:"var(--wd-t1)", fontWeight:500 }}>{value}</span>
    </div>
  );
}